import React, { useState } from "react";
import { Modal, Table, Button, Divider, Pagination, Popconfirm } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { makeAuditModeFalse } from "../../../../redux/Admin/adminActions";
import {
  useGetAutitTrails,
  useTruncateAuditTrails,
} from "../../../../hooks/useGetAuditTrails";

import { MdOutlineHistory } from "react-icons/md";
import { BsBoxArrowInRight, BsBoxArrowRight, BsXLg } from "react-icons/bs";

import SkeletonUI from "../loaders/SkeletonUI";
import "../../../../styles/superAdmin/auditModal.css";
import { AiOutlineClear } from "react-icons/ai";
import ErrorPopup from "./ErrorModal";
import SuccessPopup from "./SuccessModal";
import { formatDateTime } from "../../../util/helpers";

const getActionBadge = (action = "") => {
  const lower = action.toLowerCase();

  let cls = "default";
  let Icon = null;

  if (lower === "login") {
    cls = "login1";
    Icon = BsBoxArrowInRight;
  } else if (lower === "logout") {
    cls = "logout";
    Icon = BsBoxArrowRight;
  } else if (lower.includes("fetch all documents")) {
    cls = "fetch-docs";
  } else if (lower.includes("document approved")) {
    cls = "doc-approved";
  } else if (lower.includes("document uploaded")) {
    cls = "doc-uploaded";
  } else if (lower.includes("document upload")) {
    cls = "doc-upload";
  }

  return (
    <span className={`audit-badge ${cls}`}>
      {Icon && <Icon size={12} />}
      {action}
    </span>
  );
};

const ActorCell = ({ actor, isPerformedBy = true }) => {
  if (!actor) {
    return <span className="audit-system">System</span>;
  }

  return (
    <span className="audit-actor-text">
      {actor.fullname || actor.first_name || "Unknown"}
    </span>
  );
};

const EmptyState = () => (
  <div className="audit-empty">
    <p>No audit records found</p>
    <span>Activity will appear here</span>
  </div>
);

export default function AuditTrailModal({ selectedClient }) {
  const dispatch = useDispatch();
  const auditMode = useSelector((state) => state.admin.auditMode);
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [tableData, setTableData] = useState(null);

  const clientId = selectedClient?.id;

  const {
    data: auditData,
    isLoading,
    refetch: auditRefetch,
  } = useGetAutitTrails(clientId, auditMode && !!clientId, currentPage);

  const { mutate: clearAuditTrails, isLoading: trucatePending } =
    useTruncateAuditTrails();

  const records = tableData !== null ? tableData : auditData?.data || [];
  const total = tableData !== null ? 0 : parseInt(auditData?.total) || 0;
  const perPage = parseInt(auditData?.per_page) || 10;
  const start = (currentPage - 1) * perPage + 1;
  const end = Math.min(currentPage * perPage, total);

  const handleClose = () => {
    dispatch(makeAuditModeFalse());
    setTableData(null);
    setCurrentPage(1);
  };

  const handleClear = () => {
    clearAuditTrails(clientId, {
      onSuccess: (data) => {
        setSuccessMessage(data?.message || "Audit trails cleared successfully");
        auditRefetch();
      },
      onError: (error) => {
        setErrorMessage(
          error?.response?.data?.message || "Failed to clear audit trails",
        );
      },
    });
  };

  const handleClearClick = () => {
    Modal.confirm({
      title: "Clear Audit Trails",
      content: "Are you sure you want to perform this action?",
      okText: "Yes",
      cancelText: "No",
      onOk: handleClear,
      centered: true,
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setTableData(null);
  };

  const columns = [
    {
      title: "#",
      width: 50,
      render: (_, __, i) => (
        <span className="audit-index">
          {(currentPage - 1) * perPage + i + 1}
        </span>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: getActionBadge,
    },
    {
      title: "Performed By",
      render: (_, r) => <ActorCell actor={r.action_by} />,
    },
    {
      title: "Action On",
      render: (_, r) => <ActorCell actor={r.action_on} />,
    },
    {
      title: "Time",
      dataIndex: "created_at",
      render: formatDateTime,
    },
  ];

  return (
    <Modal
      open={auditMode}
      onCancel={handleClose}
      footer={null}
      width={900}
      className="audit-modal"
      destroyOnClose
      centered
      closable={false}
      style={{ top: 40 }}
    >
      <div className="audit-header">
        <div className="audit-title">
          <MdOutlineHistory size={18} />
          <div>
            <h3>Audit Trail</h3>
            <p className="audit-subtitle">
              {selectedClient?.name || "Client activity log"}
            </p>
          </div>
        </div>

        <Button
          className="audit-clear-btn"
          onClick={handleClearClick}
          loading={trucatePending}
        >
          <AiOutlineClear />
        </Button>
      </div>

      <Divider />

      {successMessage && (
        <SuccessPopup
          message={successMessage}
          onClose={() => setSuccessMessage(null)}
        />
      )}
      {errorMessage && (
        <ErrorPopup
          message={errorMessage}
          onClose={() => setErrorMessage(null)}
        />
      )}

      {isLoading ? (
        <SkeletonUI number={2} />
      ) : (
        <>
          <div className="audit-table-wrap">
            <Table
              columns={columns}
              dataSource={records}
              rowKey="id"
              pagination={false}
              locale={{ emptyText: <EmptyState /> }}
              className="audit-table"
            />
          </div>

          <div className="audit-pagination">
            {total > perPage && (
              <Pagination
                current={currentPage}
                pageSize={perPage}
                total={total}
                onChange={handlePageChange}
                size="small"
              />
            )}
          </div>
        </>
      )}

      <Divider />

      <div className="audit-footer">
        <span>
          {records.length
            ? `Showing ${start}-${end} of ${total}`
            : "No records"}
        </span>

        <Button
          type="primary"
          size="large"
          onClick={handleClose}
          icon={<BsXLg size={14} />}
          className="audit-close-btn-primary"
        >
          Close
        </Button>
      </div>
    </Modal>
  );
}
